/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * TdAcceptance — sidebar card listing pending / waiting acceptance
 * entries. Two instances are mounted by `TdDashboard`: one with
 * `type="waiting"` (people awaiting your acceptance) and one with
 * `type="pending"` (your acceptances still in flight). The `title`
 * attribute drives the card heading.
 *
 * Lit light-DOM component (#206). Per-name list items reach the
 * template via Lit's `${...}` text-binding, which always sets
 * `textContent` (never HTML) so server-supplied names can't smuggle
 * markup.
 */

import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import {
  fetchAcceptanceData,
  type AcceptanceList,
} from '../services/dashboard.js';
import { t, LocaleController } from '../services/i18n.js';

@customElement('td-acceptance')
export class TdAcceptance extends LitElement {
  // `override` because LitElement extends HTMLElement, whose own
  // `title` reflects to/from the `title` attribute. The `@property`
  // decorator below replaces that with Lit's reactive descriptor.
  // `reflect: true` so property writes (`comp.title = ...`) update
  // the attribute — matches the vanilla setter's behaviour, which
  // call sites (and tests) depend on.
  @property({ type: String, reflect: true }) override title = 'loading...';
  @property({ type: String, reflect: true }) type = 'loading';

  @state() acceptanceData: AcceptanceList | undefined = undefined;

  /** Re-renders this component when the UI locale changes (#89). */
  readonly localeController = new LocaleController(this);

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    console.log(
      'td-acceptance: Component connected, loading acceptance data...'
    );
    void this.loadAcceptanceData();
  }

  async loadAcceptanceData(): Promise<void> {
    console.log('td-acceptance: Fetching acceptance data from API...');
    this.acceptanceData = await fetchAcceptanceData(this.type);

    console.log('td-acceptance: Acceptance data loaded:', this.acceptanceData);
  }

  override render(): TemplateResult {
    if (!this.acceptanceData) {
      return html`
        <wa-card class="acceptance-card">
          <h2 slot="header">${this.title}</h2>
          <p>${t('acceptance.loading')}</p>
        </wa-card>
      `;
    }

    if (this.acceptanceData.length === 0) {
      return html`
        <wa-card class="acceptance-card">
          <h2 slot="header">${this.title}</h2>
          <ul class="wa-stack wa-gap-3xs">
            <li class="wa-caption-s">${t('acceptance.none')}</li>
          </ul>
        </wa-card>
      `;
    }

    return html`
      <wa-card class="acceptance-card">
        <h2 slot="header">${this.title}</h2>
        <ul class="wa-stack wa-gap-3xs">
          ${this.acceptanceData.map(
            name => html`<li class="wa-heading-s">${name}</li>`
          )}
        </ul>
      </wa-card>
    `;
  }
}
